import React from 'react';
import { formatDate } from '@happenings/components/post';
import classNames from 'classnames';
import { Link, useNavigate } from 'react-router-dom';
import { Post } from '@happenings/components/post';

import { AGE_RESTRICTION_DISPLAY } from '../../constants/ageRestrictions';
import ProgressiveImage from '../util/ProgressiveImageLoader';
import { RSVPList } from './AttendanceList';
import { Attendance } from '@happenings/components/entities/attendance';
import { Save } from '@happenings/components/entities/saves';
import UserList from '../common/UserList';
import PostComments from './PostComments';


type Props = {
  post: Post,
  postIsStarred: boolean,
  userIsAttending: boolean,
  updateAttending: (attending: boolean) => void,
  userIsAuthor: boolean,
  detailViewOpen: boolean,
  toggleDetailView: () => void,
  imageHeightPx: number,
  postImageRef: React.RefObject<HTMLImageElement>,
  updateStar: (starred: boolean) => void,
  attendees: Attendance[],
  saves: Save[],
  numComments: number,
};

const PostImageDetailToggler = (props: Props) => {
  const { detailViewOpen, toggleDetailView } = props;
  const [ listOpen, setListOpen ] = React.useState<'rsvp' | 'save' | 'comments' | null>(null);
  // open detail view -> open list view -> close list view -> close detail view
  const handleToggleView = () => {
    if (detailViewOpen && listOpen) {
      setListOpen(null);
    } else {
      toggleDetailView();
    }
  };

  // props & derived vars for post actions
  const {
    post,
    userIsAuthor,
    postImageRef,
    imageHeightPx,
  } = props;

  const {
    title,
    description,
    location,
    eventTimestamp,
    isAccessible,
    ageRestriction,
    publicUrl,
  } = post;

  const dateString = formatDate(eventTimestamp);

  const navigation = useNavigate();

  const CloseButton = ({ onClick }: { onClick: (e: React.MouseEvent) => void; }) => (
    <div
      onClick={onClick}
      style={{ textAlign: 'right', paddingBottom: 10, cursor: 'pointer', fontSize: '2em' }}
    >
      ×
    </div>
  );

  const AttendeeView = () => (
    <div style={{ position: 'relative' }} onClick={(e) => e.stopPropagation()}>
      <CloseButton onClick={(e) => { e.stopPropagation(); handleToggleView(); }} />
      {post.isPrivate ? (
        <RSVPList attendees={props.attendees} isPrivate />
      ) : (
        <UserList list={props.attendees} />
      )}
    </div>
  );

  const SaveListView = () => (
    <div style={{ position: 'relative' }} onClick={(e) => e.stopPropagation()}>
      <CloseButton onClick={(e) => { e.stopPropagation(); handleToggleView(); }} />
      <UserList list={props.saves} />
    </div>
  );

  const CommentListView = () => (
    <div style={{ position: 'relative' }} onClick={(e) => e.stopPropagation()}>
      <CloseButton onClick={(e) => { e.stopPropagation(); handleToggleView(); }} />
      <PostComments postId={post.id} />
    </div>
  );

  const DetailView = () => {
    return (
      <div role="button"
        onClick={handleToggleView}
        className="toggle-area detail-view"
        style={{ minHeight: `${imageHeightPx}px` }}
      >
        {listOpen === 'rsvp' && <AttendeeView />}
        {listOpen === 'save' && <SaveListView />}
        {listOpen === 'comments' && <CommentListView />}
        {listOpen === null && <DetailContent />}
      </div>
    );
  };

  const DetailContent = () => (
    <div>
      <div>
        <div className="event-title">{title}</div>

        <div className="detail-row">
          {/* <span className="detail-header">Date: </span> */}
          <span>{dateString}</span>
        </div>
        <div className="detail-row transparent-text">
          {/* <span className="detail-header">Location: </span> */}
          <span>{location}</span>
        </div>

        <div className="detail-row">
          <p className="detail-header">
            {AGE_RESTRICTION_DISPLAY[ ageRestriction ]}
          </p>
        </div>
        {isAccessible && <div className="detail-row">
          <p className="detail-header">
            Wheelchair Accessible
          </p>
        </div>}
        {description && <div className="detail-row">
          {/* <span className="detail-header">Description: </span> */}
          <span>{description}</span>
        </div>}
        <div className="counts" style={{ display: 'flex', gap: '10px' }}>
          {
            props.attendees.length ? (
              <div className="detail-row" style={{ position: 'relative', zIndex: 1 }}>
                <span
                  onClick={(e) => { e.stopPropagation(); setListOpen('rsvp'); }}
                  className="detail-header">
                  <b>{props.attendees.length}</b> <u>attending</u>
                </span>
              </div>
            ) : null
          }
          {
            props.saves.length ? (
              <div className="detail-row" style={{ position: 'relative', zIndex: 1 }}>
                <span
                  onClick={(e) => { e.stopPropagation(); setListOpen('save'); }}
                  className="detail-header">
                  <b>{props.saves.length}</b> <u>saved</u>
                </span>
              </div>
            ) : null
          }
          {
            <div className="detail-row" style={{ position: 'relative', zIndex: 1 }}>
              <span
                onClick={(e) => { e.stopPropagation(); setListOpen('comments'); }}
                className="detail-header">
                  {props.numComments > 0 ? (
                    <span>
                      {props.numComments} <u>{props.numComments === 1 ? 'comment' : 'comments'}</u>
                    </span>
                  ) : (
                    <span>
                      <u>Add a comment</u>
                    </span>
                  )}
              </span>
            </div>
          }
        </div>
      </div>

      {/* Post actions section */}
      <div>
        <div className="post-stats-container">
          <div className="stat-container">
            {/* Copy post
            <CopyEventLink postId={post.id} />
            <button
              className={starBtnClass}
              type="button"
              // toggles the current val of isStarred
              onClick={() => updateStar(postIsStarred)}
            >
              {startBtnText}
            </button>

            <button
              className={attendBtnClass}
              type="button"
              // toggles the current val of isAttending
              onClick={() => updateAttending(userIsAttending)}
            >
              {attendBtnTxt}
            </button> */}
            {/* Invite users to post */}
            <div className="stat-container">
              {/* Edit post */}
              {userIsAuthor
                && (
                  <button
                    className="post-btn item"
                    type="button"
                    style={{ minWidth: 60 }}

                  >
                    <Link to={`/event/${post.id}/edit`}>
                      Edit
                    </Link>
                  </button>
                )}
              {/* Delete post */}
              {userIsAuthor
                && (
                  <button className="post-btn item" type="button" style={{ minWidth: 60 }}>
                    Delete
                  </button>
                )
              }
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const previewUrl = publicUrl.includes('/full/')
    ? publicUrl.replace('/full/', '/preview/')
    : '';

  return (
    <div className="post-detail-container">
      {detailViewOpen
        ? <DetailView />
        : (
          <div
            role="button"
            className="toggle-area"
            onClick={handleToggleView}
          >
            <ProgressiveImage
              src={publicUrl}
              placeholderSrc={previewUrl}
              ref={postImageRef}
              alt={title}
            />
          </div>
        )}
    </div>
  );
};

export default PostImageDetailToggler;